import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { WebcamFloatingIcon } from './WebcamFloatingIcon';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg";

// Fallback images in case DB is empty
const fallbackImages = [
  {
    id: 1,
    url: 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/xf3js5gj_ARAVELL_CADI_HOME_1920x900_USER_EXACT%20%281%29.jpeg',
    alt: 'UGOLF ARAVELL Andorra - Vista panoràmica del Cadí amb neu'
  },
  {
    id: 2,
    url: 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/vjvfvv8q_ARAVELL_HOME_SLIDE2_OPEN_COURSE_1920x900%20%281%29.jpeg',
    alt: 'UGOLF ARAVELL Andorra - Casa club i camp estiu'
  },
  {
    id: 3,
    url: 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/fb0ofzll_ARAVELL_HOME_SLIDE3_GREEN13_1920x900_FINAL.jpeg',
    alt: 'UGOLF ARAVELL Andorra - Green 13 tardor'
  },
  {
    id: 4,
    url: 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ty7wtk5a_ARAVELL_HOME_SLIDE4_FLAG_AUTUMN_1920x900.jpeg',
    alt: 'UGOLF ARAVELL Andorra - Casa club tardor'
  },
  {
    id: 5,
    url: 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/il2mkjgh_ARAVELL_HOME_SLIDE5_LIFESTYLE_CAP_1920x900.jpeg',
    alt: 'UGOLF ARAVELL Andorra - Lifestyle gorra'
  },
  {
    id: 6,
    url: 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/rodb0dd0_ARAVELL_HOME_SLIDE6_ACTIVITY_1920x900.jpeg',
    alt: 'UGOLF ARAVELL Andorra - Activitat al camp'
  },
  {
    id: 7,
    url: 'https://customer-assets.emergentagent.com/job_64ec45a5-bc37-45d2-aeb5-c91441d6b899/artifacts/ywu3xteb_Aravell%20Green18.jpg',
    alt: 'UGOLF ARAVELL Andorra - Green 18 amb muntanyes nevades'
  },
  {
    id: 8,
    url: 'https://customer-assets.emergentagent.com/job_64ec45a5-bc37-45d2-aeb5-c91441d6b899/artifacts/eakq5zoz_Aravell%20Green9%20casaclub%202.jpg',
    alt: 'UGOLF ARAVELL Andorra - Casa Club des del green 9'
  },
  {
    id: 9,
    url: 'https://customer-assets.emergentagent.com/job_64ec45a5-bc37-45d2-aeb5-c91441d6b899/artifacts/puqhjrl2_Aravell%20Green18.jpg',
    alt: 'UGOLF ARAVELL Andorra - Vista del green 18 amb Pirineus'
  },
  {
    id: 10,
    url: 'https://customer-assets.emergentagent.com/job_64ec45a5-bc37-45d2-aeb5-c91441d6b899/artifacts/0c50rmmw_AravellDrivingRange.jpg',
    alt: 'UGOLF ARAVELL Andorra - Camp de pràctiques amb muntanyes'
  }
];

export const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [heroImages, setHeroImages] = useState(fallbackImages);
  const { language } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    duration: 50, // Slower transition
    dragFree: false
  });

  // Fetch carousel images from API
  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/carousel?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          if (data.images && data.images.length > 0) {
            setHeroImages(data.images);
          }
        }
      } catch (error) {
        console.error('Failed to fetch carousel images:', error);
        // Keep fallback images on error
      }
    };
    fetchCarouselImages();
  }, []);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  // Auto-advance carousel - 7 seconds between slides
  useEffect(() => {
    if (!emblaApi) return;
    
    const autoplay = setInterval(() => {
      emblaApi.scrollNext();
    }, 7000);

    return () => clearInterval(autoplay);
  }, [emblaApi]);

  return (
    <section className="relative h-screen min-h-[600px] w-full overflow-hidden" data-testid="hero-carousel">
      {/* Carousel container */}
      <div className="absolute inset-0" ref={emblaRef}>
        <div className="flex h-full">
          {heroImages.map((image, index) => (
            <div 
              key={image.id} 
              className="flex-[0_0_100%] min-w-0 relative h-full"
            >
              <div className="absolute inset-0 overflow-hidden">
                <motion.img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1 }}
                  animate={{ 
                    scale: currentIndex === index ? 1.1 : 1 
                  }}
                  transition={{ 
                    duration: 12, 
                    ease: 'easeOut' 
                  }}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />
            </div>
          ))}
        </div>
      </div>

      {/* Webcam Floating Icon - Top Right */}
      <WebcamFloatingIcon />

      {/* Navigation arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors duration-200 text-white"
        aria-label="Previous slide"
        data-testid="hero-prev-btn"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors duration-200 text-white"
        aria-label="Next slide"
        data-testid="hero-next-btn"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Logo institucional + Course Designer - bottom left */}
      <motion.div
        className="absolute bottom-24 left-4 sm:bottom-28 md:bottom-24 md:left-16 z-20 flex items-center gap-2 sm:gap-3 md:gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
      >
        {/* Aravell Logo */}
        <img
          src={LOGO_URL}
          alt="UGOLF Aravell Andorra"
          className="w-[70px] sm:w-[90px] md:w-[14vw] md:min-w-[140px] md:max-w-[220px] h-auto opacity-95 drop-shadow-xl"
          data-testid="hero-logo"
        />
        {/* Separator line */}
        <div className="h-8 sm:h-10 md:h-16 w-px bg-white/40" />
        {/* Course Designer Attribution */}
        <div className="text-white drop-shadow-lg w-[70px] sm:w-[90px] md:w-[14vw] md:min-w-[140px] md:max-w-[220px]">
          <p className="text-[7px] sm:text-[8px] md:text-xs tracking-[0.15em] sm:tracking-[0.2em] md:tracking-[0.25em] uppercase opacity-70">
            {language === 'ca' ? 'Disseny' : language === 'es' ? 'Diseño' : language === 'fr' ? 'Design' : 'Course Design'}
          </p>
          <p className="text-[10px] sm:text-xs md:text-base lg:text-lg font-display font-medium tracking-[0.03em] sm:tracking-[0.05em] md:tracking-[0.08em] mt-0.5 md:mt-1">
            José María Olazábal
          </p>
        </div>
      </motion.div>

      {/* Dots indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="hero-dots">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              currentIndex === index 
                ? 'w-8 bg-white' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            data-testid={`hero-dot-${index}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:block">
        <motion.div 
          className="flex flex-col items-center gap-2 text-white/70"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <span className="text-xs tracking-[0.2em] uppercase">Scroll</span>
          <div className="w-px h-8 bg-white/50" />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroCarousel;
